import { Injectable } from '@angular/core';
import { filter } from 'rxjs';
import { NotificationsService } from '../components/notification/NotificationsService';
import { NetworkStatusService } from './network-status.service';
import { PushFailure, PushService } from './push.service';
import { PwaUpdateService } from './pwa-update.service';

/** Message affiché à l'utilisateur pour chaque cause d'échec des notifications push. */
const PUSH_FAILURE_MESSAGES: Record<PushFailure, { title: string; content: string }> = {
  'service-worker-unavailable': {
    title: 'Notifications indisponibles',
    content: 'L’application n’est pas encore prête. Réessayez dans quelques instants.',
  },
  'subscription-rejected': {
    title: 'Activation impossible',
    content: 'Votre navigateur a refusé l’abonnement aux notifications.',
  },
  'registration-failed': {
    title: 'Activation non enregistrée',
    content: 'L’abonnement n’a pas pu être transmis. Réessayez plus tard.',
  },
  'unregistration-failed': {
    title: 'Désactivation impossible',
    content: 'La désactivation n’a pas pu être transmise. Réessayez plus tard.',
  },
  'unsubscribe-failed': {
    title: 'Désactivation incomplète',
    content: 'Votre navigateur n’a pas pu supprimer l’abonnement.',
  },
  'unexpected-error': {
    title: 'Notifications indisponibles',
    content: 'L’activation des notifications a été interrompue.',
  },
};

/**
 * Relie les évènements PWA transitoires (mise à jour disponible, perte de
 * connexion, échec d'activation des notifications) au système de toasts existant
 * (`NotificationsService`).
 *
 * L'invite d'installation (Android / iOS) n'est PAS gérée ici : elle a ses
 * propres composants dédiés (PwaInstallBannerComponent, PwaIosInstallSheetComponent).
 */
@Injectable({ providedIn: 'root' })
export class PwaNotificationsService {
  private offlineNotificationId: string | null = null;
  private updateNotified = false;

  constructor(
    private readonly notifications: NotificationsService,
    private readonly pwaUpdate: PwaUpdateService,
    private readonly network: NetworkStatusService,
    private readonly push: PushService,
  ) {}

  init(): void {
    this.pwaUpdate.updateReady$
      .pipe(filter(ready => ready))
      .subscribe(() => this.showUpdateNotification());

    this.network.online$.subscribe(online =>
      online ? this.clearOfflineNotification() : this.showOfflineNotification(),
    );

    this.push.failures$.subscribe(failure => this.showPushFailure(failure));
  }

  private showPushFailure(failure: PushFailure): void {
    const message = PUSH_FAILURE_MESSAGES[failure];
    this.notifications.error(message.title, message.content);
  }

  private showUpdateNotification(): void {
    if (this.updateNotified) {
      return;
    }
    this.updateNotified = true;
    const notif = this.notifications.info(
      'Nouvelle version disponible',
      'Cliquez ici pour mettre à jour l’application.',
      { timeOut: 0, clickToClose: true },
    );
    notif.click?.subscribe(() => this.pwaUpdate.applyUpdate());
  }

  private showOfflineNotification(): void {
    if (this.offlineNotificationId) {
      return;
    }
    const notif = this.notifications.warn(
      'Hors ligne',
      'La connexion réseau est indisponible.',
      { timeOut: 0, clickToClose: false },
    );
    this.offlineNotificationId = notif.id ?? null;
  }

  private clearOfflineNotification(): void {
    if (!this.offlineNotificationId) {
      return;
    }
    this.notifications.remove(this.offlineNotificationId);
    this.offlineNotificationId = null;
  }
}
